/**
 * Parcours des parties publiques.
 *
 * La liste est un instantane demande explicitement : pas d'abonnement temps
 * reel. Un salon de lobby bouge peu, et pousser chaque changement a tous les
 * navigateurs qui regardent la liste couterait bien plus que ca ne rapporte.
 * Rafraichissement manuel + automatique toutes les 10 s, uniquement quand
 * l'onglet est visible.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  CATEGORIES,
  ROOM_PAGE_SIZE,
  hasFixedRounds,
  totalRounds,
  type GameMode,
  type RoomSettings,
  type RoomSummary,
} from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { navigate } from '../router.js';
import { Screen } from '../ui/Screen.js';
import { MODE_LABEL, ZONE_SIZE_SELECTABLE } from './GameSettings.js';
import { usePublicConfig } from '../publicConfig.js';
import { DiscordIcon } from '../ui/icons.js';

const REFRESH_MS = 10_000;

export function BrowseScreen(): ReactElement {
  const t = useT();
  const [rooms, setRooms] = useState<readonly RoomSummary[] | null>(null);
  /** Nombre de salons listables cote serveur, plafond compris. */
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Le lien vient du SERVEUR : sans lien configure, pas de bouton, plutot
  // qu'un bouton qui ne mene nulle part.
  const { discordInvite } = usePublicConfig();
  const refresh = useCallback(() => connection.send({ type: 'listRooms' }), []);

  useEffect(() => {
    const off = connection.on('message', (message) => {
      if (message.type === 'roomList') {
        setRooms(message.rooms);
        setTotal(message.total);
      }
    });
    refresh();

    const timer = window.setInterval(() => {
      // Un onglet en arriere-plan n'a aucune raison d'interroger le serveur.
      if (!document.hidden) refresh();
    }, REFRESH_MS);

    const onVisible = (): void => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      off();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // On NAVIGUE, on ne rejoint pas. `RoomScreen` est le seul a joindre, et ce
  // n'est pas qu'un rangement : joindre ici puis laisser l'ecran suivant
  // joindre a son tour envoyait deux `join` sur la meme socket, ce que le
  // serveur refuse (« deja dans un salon »). Le pseudo n'est pas perdu pour
  // autant, `RoomScreen` le lit dans le meme store, et il le NETTOIE, ce que
  // cet appel-ci ne faisait pas.
  const join = useCallback((code: string) => {
    navigate(`/room/${code}`);
  }, []);

  const pageCount = Math.max(Math.ceil((rooms?.length ?? 0) / ROOM_PAGE_SIZE), 1);
  // La liste se rafraichit toute seule : une page qui disparait ne doit pas
  // laisser l'ecran vide avec des boutons inertes.
  const current = Math.min(page, pageCount - 1);
  useEffect(() => {
    if (page !== current) setPage(current);
  }, [page, current]);

  const visible = useMemo(
    () => (rooms ?? []).slice(current * ROOM_PAGE_SIZE, (current + 1) * ROOM_PAGE_SIZE),
    [rooms, current],
  );

  return (
    <Screen name="browse" scroll>
      <main className="browse">
        <header className="browse__header">
          <h1 className="browse__title">{t('browse.title')}</h1>
          <button type="button" className="button-ghost" onClick={refresh}>
            {t('browse.refresh')}
          </button>
        </header>

        {rooms !== null && rooms.length === 0 && (
          <div className="browse__empty">
            <p>{t('browse.empty')}</p>
            <button type="button" className="button-primary" onClick={() => navigate('/')}>
              {t('browse.create')}
            </button>
          </div>
        )}

        {rooms !== null && rooms.length > 0 && (
          <p className="browse__count">
            {total > rooms.length
              ? t('browse.truncated', { shown: rooms.length, total })
              : t('browse.count', { shown: rooms.length })}
          </p>
        )}

        <ul className="browse__list">
          {visible.map((room) => (
            <li className="room-card" key={room.code}>
              <div className="room-card__top">
                <span className="room-card__host">{room.hostNickname}</span>
                <span className="room-card__players mono-tabular">
                  {t('browse.players', { count: room.playerCount, max: room.maxPlayers })}
                </span>
              </div>

              <SettingsChips settings={room.settings} playerCount={room.playerCount} />

              <div className="room-card__actions">
                <span className="room-card__code mono-tabular">{room.code}</span>
                <button type="button" className="button-primary" onClick={() => join(room.code)}>
                  {t('browse.join')}
                </button>
              </div>
            </li>
          ))}
        </ul>

        {pageCount > 1 && (
          <nav className="browse__pager" aria-label={t('browse.title')}>
            <button
              type="button"
              className="button-ghost"
              onClick={() => setPage(Math.max(current - 1, 0))}
              disabled={current === 0}
            >
              {t('browse.prev')}
            </button>
            <span className="browse__page mono-tabular">
              {t('browse.page', { n: current + 1, total: pageCount })}
            </span>
            <button
              type="button"
              className="button-ghost"
              onClick={() => setPage(Math.min(current + 1, pageCount - 1))}
              disabled={current >= pageCount - 1}
            >
              {t('browse.next')}
            </button>
          </nav>
        )}

        {/* Sous la liste et pas sous le titre : c'est APRES avoir parcouru les
            salons qu'on constate qu'il n'y a personne, et c'est la que la
            proposition a un sens. Place en tete, elle detournerait de la liste
            qu'on vient justement ouvrir. */}
        {discordInvite !== null && (
          <a
            className="button-ghost button-block button-icon"
            href={discordInvite}
            target="_blank"
            rel="noreferrer noopener"
          >
            <DiscordIcon />
            {t('browse.discord')}
          </a>
        )}

        <button type="button" className="button-ghost button-block" onClick={() => navigate('/')}>
          {t('common.back')}
        </button>
      </main>
    </Screen>
  );
}

/** Les six reglages, en pastilles compactes. */
/** Un signe par mode, pour reperer d'un coup d'œil dans la liste. */
const MODE_ICON: Record<GameMode, string> = {
  hunt: '◈',
  classic: '◎',
  oneDraws: '☝',
  battle: '☠',
  stream: '▶',
  contest: '★',
  // Deux mains : on dessine a plusieurs sur le meme tableau.
  collab: '⁂',
};

function SettingsChips({
  settings,
  playerCount,
}: {
  readonly settings: RoomSettings;
  readonly playerCount: number;
}): ReactElement {
  const t = useT();
  const chips: Array<{ icon: string; label: string }> = [
    // Le mode d'abord : c'est ce qui change le plus la partie qu'on rejoint.
    { icon: MODE_ICON[settings.mode], label: t(MODE_LABEL[settings.mode]) },
  ];

  // Le nombre de manches REEL, et seulement quand il en existe un. En « un seul
  // dessine », `settings.rounds` compte des TOURS : afficher 3 pour une partie
  // de 18 manches serait le mensonge qu'on vient de corriger dans le salon.
  // L'effectif COURANT est la meilleure estimation disponible, et c'est celui
  // qu'affiche deja la carte juste au-dessus. En battle royale il n'y a rien a
  // annoncer : la partie s'arrete quand il ne reste qu'un joueur.
  if (hasFixedRounds(settings.mode)) {
    chips.push({
      icon: '⟳',
      label: t('browse.rounds', { n: totalRounds(settings.mode, settings.rounds, playerCount) }),
    });
  }

  chips.push(
    { icon: '✎', label: t('browse.drawTime', { n: settings.drawTime }) },
    { icon: '⌖', label: t('browse.guessTime', { n: settings.guessTime }) },
  );
  if (ZONE_SIZE_SELECTABLE) {
    chips.push({ icon: '▣', label: t('browse.zone', { n: Math.round(settings.zoneSize * 100) }) });
  }

  // Categories : annoncees SEULEMENT si le salon en a retire. Toutes cochees est
  // l'etat par defaut, l'afficher n'apprendrait rien et allongerait la carte.
  // Le compte plutot que la liste des noms : a quatre categories sur cinq, la
  // liste passe a la ligne et noie le reste des reglages.
  if (settings.categories.length < CATEGORIES.length) {
    chips.push({
      icon: '◫',
      label: t('browse.categories', {
        n: settings.categories.length,
        total: CATEGORIES.length,
      }),
    });
  }

  return (
    <ul className="chips">
      {chips.map((chip) => (
        <li className="chip" key={chip.label}>
          <span className="chip__icon" aria-hidden="true">
            {chip.icon}
          </span>
          {chip.label}
        </li>
      ))}
    </ul>
  );
}
